import React, { FC, useState } from 'react';
import { Input, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { get, set } from 'lodash';
import produce from 'immer';
import { DefaultOptionType } from 'antd/lib/select';
import {
  Checkbox,
  CompanyTypeSelect,
  DateInput,
  DocumentInput,
  EmailFormItem,
  LegalTypeSelect,
  LtrInput,
  IssuingAuthoritySelect,
  Select,
} from '~/components';
import Form from '~/components/WizardForm';
import InputsGroupLayout from '~/layouts/InputsGroupLayout';
import buildNamePath from '~/utils/buildNamePath';

import { getDecodedJwt, isAgent } from '~/utils/getDecodedJwt';
import { Draft } from '~/types/Draft';
import Section from '~/layouts/Section ';
import { useGetApplication } from '~/controllers/wizard';
import { useAmendment } from '~/controllers/amendment';
import { CompanyTypeReq } from '~/types/CompanyType';
import { handleCompanyType } from '~/utils/handleCompanyType';
import { useHandbook } from '~/controllers/handbook';
import CustomFields from '~/components/CustomFields';
import { useRefScrollTo } from '~/hooks/useRefScrollTo';
import theme from '~/constants/theme';
import { capitalizeWords } from '~/utils/validateCharacters';

const CompanyInformation: FC = () => {
  const { t } = useTranslation('fillApplication');
  const ref = useRefScrollTo();
  const applicationQuery: any = useGetApplication();
  const handbookQuery = useHandbook();
  const { isAmendment } = useAmendment();
  const [form] = Form.useForm();
  const [tradeLicenseIssuingAuthorityOptions, setTradeLicenseIssuingAuthorityOptions] = useState<
    DefaultOptionType[] | undefined
  >();
  const [isLoading, setLoading] = useState<boolean>(false);

  const handbook = handbookQuery.data?.handbooks;
  const agent = isAgent();
  const showCompanyType = agent;

  const onValuesChange = (changedValues: CompanyTypeReq, formValues: any) => {
    if (!handbook) return;
    handleCompanyType(form, handbook, (name) => name)(changedValues, formValues);
  };

  const isNewClient =
    get(applicationQuery?.data, 'payload.isNewClient') ||
    get(applicationQuery?.data, 'errorKeys')?.includes('application.companyInformation');

  if (!applicationQuery.data) return null;
  return (
    <Container className="CompanyInformation" ref={ref}>
      <Form<Draft<'companyInformation'>>
        stepKey="companyInformation"
        setInitialValues={(initialValues) => {
          const { companyType } = Object.keys(initialValues).length > 0 ? initialValues : applicationQuery.data.payload;

          const result = produce({ ...initialValues, companyType }, (draft) => {
            const decodedToken = getDecodedJwt();
            const companyLegalName = decodedToken?.company_legal_name || get(draft, n(['companyLicense', 'legalName']));
            const licenseNumber = decodedToken?.trade_license_number;

            if (!get(draft, n(['companyLicense', 'legalName']))) {
              set(draft, n(['companyLicense', 'legalName']), companyLegalName);
            }
            if (!get(draft, n(['email']))) {
              set(draft, n(['email']), applicationQuery.data?.payload.initiatorEmail);
            }
            if (!get(draft, n(['companyLicense', 'licenseNumber']))) {
              set(draft, n(['companyLicense', 'licenseNumber']), licenseNumber);
            }
          });

          return result;
        }}
        onValuesChange={onValuesChange}
        form={form}
        parseBeforeSubmit={(values: any) => {
          const authorityName = handbook
            ?.find((item) => item.key === 'tradeLicenseIssuingAuthority')
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            ?.values?.find((item) => item?.apiValue === values?.companyInformation?.companyLicense?.licenseAuthority);
          return {
            companyInformation: {
              ...values?.companyInformation,
              companyLicense: {
                ...values?.companyInformation?.companyLicense,
                licenseAuthorityName: authorityName?.localizedValue,
              },
            },
          };
        }}
      >
        <Typography.Title>{t('Company Information')}</Typography.Title>
        <Typography.Paragraph>{t('Please complete the following details about your Company.')}</Typography.Paragraph>

        <Section>
          <Typography.Title level={4}>{t('Company Details').toUpperCase()}</Typography.Title>
          <InputsGroupLayout>
            <Form.ShouldUpdate shouldUpdate={() => false}>
              {(formState) => {
                const legalName = formState.getFieldValue(n(['companyLicense', 'legalName']));
                return (
                  <Form.Item
                    label={t('Company Legal Name')}
                    name={n(['companyLicense', 'legalName'])}
                    disabled={isAmendment}
                  >
                    <Input />
                  </Form.Item>
                );
              }}
            </Form.ShouldUpdate>
            <Form.Item
              label={t('Company legal type')}
              name={n(['companyLicense', 'legalType'])}
              disabled={isAmendment}
            >
              <LegalTypeSelect />
            </Form.Item>
            <Form.Item label={t('Client type')} name={n(['companyLicense', 'clientType'])} initialValue="none">
              <Select
                options={[
                  { label: 'None', value: 'None' },
                  { label: 'D2C', value: 'D2C' },
                ]}
              />
            </Form.Item>
          </InputsGroupLayout>
        </Section>

        <Section formItemLast>
          <Typography.Title level={4}>{t('TRADE LICENSE DETAILS').toUpperCase()}</Typography.Title>

          <Typography.Paragraph>{t('Trade License Document')}</Typography.Paragraph>

          <Form.Item name={n(['companyLicense', 'licenseDocument'])} optional={!isNewClient}>
            <DocumentInput name="licenseDocument" />
          </Form.Item>

          <InputsGroupLayout>
            <Form.Item label={t('City of establishment')} name={n(['companyLicense', 'city'])}>
              <Input />
            </Form.Item>
            {showCompanyType && (
              <Form.Item name={['companyType', 'companyType']} label={t('Company type')}>
                <CompanyTypeSelect hint={false} />
              </Form.Item>
            )}
            <Form.Item label={t('Trade license number')} name={n(['companyLicense', 'licenseNumber'])}>
              <LtrInput />
            </Form.Item>
            <Form.Item label={t('Issuing Authority')} name={n(['companyLicense', 'licenseAuthority'])}>
              <IssuingAuthoritySelect />
            </Form.Item>
            <Form.Item label={t('License issue date')} name={n(['companyLicense', 'licenseIssueDate'])}>
              <DateInput disableDateRule="date <= today" />
            </Form.Item>
            <Form.Item label={t('License expiry date')} name={n(['companyLicense', 'licenseExpiryDate'])}>
              <DateInput disableDateRule="date > today" />
            </Form.Item>
            <EmailFormItem label={t('Company email')} name={n(['email'])} hidden />
            {agent && <EmailFormItem label={t('Company email')} name={n(['email'])} />}
          </InputsGroupLayout>
        </Section>

        <Section>
          <Typography.Title level={4}>{t('Tax Details').toUpperCase()}</Typography.Title>
          <Typography.Paragraph>{t('VAT Certificate')}</Typography.Paragraph>

          <Form.Item name={n(['taxRegistrationCertificate', 'vatCertificate'])} optional={!isNewClient}>
            <DocumentInput name="vatCertificate" />
          </Form.Item>

          <InputsGroupLayout>
            <Form.Item
              label={capitalizeWords(t('Tax registration number'))}
              name={n(['taxRegistrationCertificate', 'number'])}
            >
              <Input />
            </Form.Item>
          </InputsGroupLayout>
        </Section>

        <Section formItemLast>
          <Typography.Title level={4}>{t('Site Visit Documents').toUpperCase()}</Typography.Title>
          <Typography.Paragraph>{t('Site Visit Report')}</Typography.Paragraph>

          <Form.Item name={n(['siteVisitDocument', 'siteVisitReport'])} optional={!isNewClient}>
            <DocumentInput name="report" />
          </Form.Item>

          <Typography.Paragraph>{t('Photo of Inside the Outlet')}</Typography.Paragraph>

          <Form.Item name={n(['siteVisitDocument', 'inside'])} optional={!isNewClient}>
            <DocumentInput name="inside" />
          </Form.Item>

          <Typography.Paragraph>{t('Photo of Outside the Outlet')}</Typography.Paragraph>

          <Form.Item name={n(['siteVisitDocument', 'outside'])} optional={!isNewClient}>
            <DocumentInput name="outside" />
          </Form.Item>
        </Section>

        {showCompanyType && (
          <Form.Item name={['companyType', 'mcc']} hidden>
            <Input />
          </Form.Item>
        )}
        <CustomFields />
      </Form>
    </Container>
  );
};

const n = buildNamePath('companyInformation');

const taxCertEnabledNamePath = n(['taxRegistrationCertificate', 'enabled']);

const Container = styled.div`
  .CompanyInformation-Checkbox {
    margin-bottom: 16px;
  }
  @media (max-width: ${theme.breakpoints.sm}px) {
    .ant-col {
      width: 1rem;
    }
  }
`;

export default CompanyInformation;
